/* eslint-disable @typescript-eslint/naming-convention */
import { useEffect } from 'react'
import { Link, useOutletContext, useParams, useNavigate } from 'react-router-dom'
import { useComputed, useSignal } from '@preact/signals-react'

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Button from 'react-bootstrap/Button'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Table from 'react-bootstrap/Table'
import Tooltip from 'react-bootstrap/Tooltip'

import AccessDeniedElement from '../../../components/AccessDeniedElement.jsx'
import AddRoleModal from './components/AddRoleModal.jsx'
import AdministrationTabs from '../components/AdministrationTabs.jsx'
import ConfirmationModal from '../../../components/ConfirmationModal.jsx'
import Icon from '../../../components/Icon.jsx'
import LoadingAnimation from '../../../components/LoadingAnimation.jsx'
import PageTitle from '../../../components/PageTitle.jsx'
import TableColumnHeader from '../../../components/TableColumnHeader.jsx'

import { NotificationTypes, addNotification } from '../../../state/actions/notificationActions'
import { deleteRole, fetchOrganizationRoleList } from '../../../state/actions/roleActions.js'

import { organizationRoleListSignal, organizationRoleListLoadedSignal } from '../../../state/signals/roleSignals.js'

import { RoleType, SortDirectionType } from '../../../utils/constants.js'
import { computeSortedData } from '../../../utils/table.js'

const RolesPage = function () {

	const navigate = useNavigate()
	const { user, config, accessToken, userPermissions, userPermissionsSummary, accordionData: sidebarAccordionData } = useOutletContext()
	
	useEffect(() => {
		if (userPermissionsSummary.value && !userPermissionsSummary.value.canViewRoles) {
			addNotification('You are not authorized to view roles for this account.', NotificationTypes.ERROR, true)
			navigate(`/`)
		}
	}, [userPermissionsSummary.value, navigate])

	const canEditComputed = useComputed(
		() => {
			return userPermissionsSummary?.value?.canEditRoles
		}
	)

	const { beId } = useParams()

	useEffect(() => {
		fetchOrganizationRoleList(beId, config, accessToken)
	}, [beId, config, accessToken])

	const sortColumnSignal = useSignal('')
	const sortDirectionSignal = useSignal(SortDirectionType.ASCENDING)

	const sortedDataComputed = useComputed(computeSortedData(organizationRoleListSignal, sortColumnSignal, sortDirectionSignal))

	const selectedRoleSignal = useSignal(null)
	const showDeleteRoleModalSignal = useSignal(false)
	const showAddRoleModalSignal = useSignal(false)

	const hideAddRoleModal = () => {
		showAddRoleModalSignal.value = false
	}

	const showDeleteRoleModal = (role) => {
		selectedRoleSignal.value = role
		showDeleteRoleModalSignal.value = true
	}
	const closeDeleteRoleModal = () => {
		selectedRoleSignal.value = null
		showDeleteRoleModalSignal.value = false
	}

	const confirmDeleteRole = (role) => {
		deleteRole(beId, role, config, accessToken)
		closeDeleteRoleModal()
		selectedRoleSignal.value = null
	}

	const renderTooltip = (props) => {
		return (
			<Tooltip {...props}>Only custom roles are eligible for deletion.</Tooltip>
		)
	}

	const RoleTableRows = function ({ sortedRoles }) {
		let rows = []
		for (let i = 0; i < sortedRoles.length; i++) {
			let role = sortedRoles[i]
			rows.push(
				<tr key={role.id}>
					<td><Link to={`/be/${beId}/management/roles/${role.id}`}>{role.name}</Link></td>
					<td>{role.userCount}</td>
					{
						userPermissionsSummary.value.canEditRoles && (
							<td>{
								role.type == RoleType.Custom ? (
									<Button className="btn btn-link" type="button" style='float:right; color:#d43f3a; font-size:20px; margin: -10px' onClick={() => { showDeleteRoleModal(role) }}>
										<Icon icon='trash-o' height={16} width={16}></Icon>
									</Button>
								) : (
									<OverlayTrigger
										placement="left"
										overlay={renderTooltip}
									>
											<Icon icon="no-trash" height={16} width={16} style={{ cursor: "disabled" }} />
									</OverlayTrigger>
								)
							}</td>
						)
					}
				</tr>
			)
		}
		return rows
	}

	const columnCountComputed = useComputed(
		() => {
			return userPermissionsSummary.value.canEditRoles ? 3 : 2
		}
	)

	return (
		<>
			<Breadcrumb className="px-3 pt-1 bg-light">
				<Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/management/accounts` }}>Administration</Breadcrumb.Item>
				<Breadcrumb.Item active>Roles</Breadcrumb.Item>
			</Breadcrumb>
			<PageTitle title='Roles' canEditSignal={canEditComputed} />
			<AdministrationTabs activeKey="3"></AdministrationTabs>
			{
				userPermissionsSummary.value.canViewRoles ? (
					<>
						<div className='panel-body'>
							<Table className="table table-striped table-hover">
								<thead>
									<tr>
										<TableColumnHeader title="Role Name" datafield='name' sortable={true} sortColumnSignal={sortColumnSignal} sortDirectionSignal={sortDirectionSignal}></TableColumnHeader>
										<TableColumnHeader title="User Count" datafield='userCount' sortable={true} sortColumnSignal={sortColumnSignal} sortDirectionSignal={sortDirectionSignal}></TableColumnHeader>
										{
											userPermissionsSummary.value.canEditRoles && (
												<th>Delete</th>
											)
										}
									</tr>
								</thead>
								<tbody>
									{
										!organizationRoleListLoadedSignal.value ? (
											<tr key="loading">
												<td colSpan={columnCountComputed.value}>
													<LoadingAnimation />
												</td>
											</tr>
										) : (
											<RoleTableRows sortedRoles={sortedDataComputed.value}></RoleTableRows>
										)
									}
								</tbody>
							</Table>
						</div>
						{
							userPermissionsSummary.value.canEditRoles && (
								<AddRoleModal showSignal={showAddRoleModalSignal} hideModal={hideAddRoleModal} />
							)
						}
						<ConfirmationModal
							show={showDeleteRoleModalSignal.value}
							handleCancel={closeDeleteRoleModal}
							handleConfirm={() => { confirmDeleteRole(selectedRoleSignal.value) }}
							message={"Deleting a Role cannot be undone. Are you sure you want to delete this role?"}
							title={"Confirm Delete"}
							confirmText={'Yes'}
						></ConfirmationModal >
					</>
				) : (
					<AccessDeniedElement />
				)
			}
		</>
	)
}
export default RolesPage